// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import simulados from '@/dados-simulados/mensagens.json';
import { type ErroApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

import type {
  AutorDaMensagem,
  ConversaDaMedicao,
  DreDaMensagem,
  MensagemDaMedicao,
} from './contrato';

/**
 * A ÚNICA porta de entrada do dado simulado na tela Mensagens.
 *
 * Exceção do CLAUDE.md de 2026-08-22 (tela cuja API ainda não existe): a
 * tabela `medicao_mensagens` e a função `get_sme_inbox_mensagens` estão no
 * banco desde a migração 0002, mas não há módulo em `apps/api/src/modulos/`
 * que as sirva. Quando a rota nascer, as três funções abaixo passam a consultar
 * o servidor, `dados-simulados/mensagens.json` sai do repositório na mesma PR e
 * a tela não muda.
 *
 * SÓ LEITURA. Enviar mensagem e marcar como lida são escrita — a tela mostra o
 * aviso de indisponibilidade e esta porta não oferece nada que grave, nem em
 * memória.
 *
 * A CAIXA DE ENTRADA DERIVA DA CONVERSA, e não o contrário. No banco a caixa é
 * uma função que agrega `medicao_mensagens`; aqui ela agrega o mesmo array que
 * a conversa lê. Duas listas soltas acabariam mostrando uma última mensagem na
 * caixa e outra ao abrir a conversa.
 */

/** Sem ele o estado "carregando" nunca aparece em tela — e não se confere. */
const LATENCIA_MS = 250;

interface MensagemSimulada {
  id: string;
  autorPapel: string;
  mensagem: string;
  horasAtras: number;
  lida: boolean;
}

interface ConversaSimulada {
  medicaoId: string;
  credenciadoId: string;
  credenciadoNome: string;
  credenciadoDocumento: string;
  dreId: string;
  mesesAtras: number;
  mensagens: MensagemSimulada[];
}

const DRES = simulados.dres as readonly DreDaMensagem[];
const CONVERSAS = simulados.conversas as readonly ConversaSimulada[];

export const CHAVES_DE_MENSAGENS = {
  caixa: ['sme-mensagens', 'caixa'] as const,
  dres: ['sme-mensagens', 'dres'] as const,
  conversa: (medicaoId: string) => ['sme-mensagens', 'conversa', medicaoId] as const,
};

function esperar(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, LATENCIA_MS);
  });
}

/**
 * Nenhuma data literal no mock: data fixa envelhece sozinha, esvazia o filtro
 * de competência e um dia derruba o teste numa PR sem relação.
 */
function horasAtras(horas: number): string {
  return new Date(Date.now() - horas * 3_600_000).toISOString();
}

function competenciaDe(mesesAtras: number): string {
  const hoje = new Date();
  const alvo = new Date(hoje.getFullYear(), hoje.getMonth() - mesesAtras, 1);
  return `${alvo.getFullYear()}-${String(alvo.getMonth() + 1).padStart(2, '0')}`;
}

function paraMensagem(medicaoId: string, simulada: MensagemSimulada): MensagemDaMedicao {
  const criadoEm = horasAtras(simulada.horasAtras);
  return {
    id: simulada.id,
    medicaoId,
    autorPapel: simulada.autorPapel as AutorDaMensagem,
    mensagem: simulada.mensagem,
    lidaEm: simulada.lida ? criadoEm : null,
    criadoEm,
  };
}

function mensagensDe(conversa: ConversaSimulada): MensagemDaMedicao[] {
  return conversa.mensagens
    .map((simulada) => paraMensagem(conversa.medicaoId, simulada))
    .sort((a, b) => a.criadoEm.localeCompare(b.criadoEm));
}

/** Monta a linha da caixa do mesmo jeito que a função do banco: última mensagem e não lidas agregadas. */
function paraConversa(conversa: ConversaSimulada): ConversaDaMedicao | null {
  const mensagens = mensagensDe(conversa);
  const ultima = mensagens.at(-1);
  const dre = DRES.find((d) => d.id === conversa.dreId);
  if (!ultima || !dre) {
    return null;
  }

  return {
    medicaoId: conversa.medicaoId,
    credenciadoId: conversa.credenciadoId,
    credenciadoNome: conversa.credenciadoNome,
    credenciadoDocumento: conversa.credenciadoDocumento,
    dreId: dre.id,
    dreCodigo: dre.codigo,
    dreNome: dre.nome,
    competencia: competenciaDe(conversa.mesesAtras),
    ultimaMensagem: ultima.mensagem,
    ultimaMensagemEm: ultima.criadoEm,
    ultimoAutorPapel: ultima.autorPapel,
    totalMensagens: mensagens.length,
    naoLidas: mensagens.filter((m) => m.autorPapel === 'CREDENCIADO' && m.lidaEm === null).length,
  };
}

export async function carregarCaixaDeMensagens(): Promise<ConversaDaMedicao[]> {
  await esperar();
  return CONVERSAS.map(paraConversa).filter((c): c is ConversaDaMedicao => c !== null);
}

export async function carregarMensagensDaMedicao(medicaoId: string): Promise<MensagemDaMedicao[]> {
  await esperar();
  const conversa = CONVERSAS.find((c) => c.medicaoId === medicaoId);
  return conversa ? mensagensDe(conversa) : [];
}

/**
 * A caixa de entrada da SME.
 *
 * `habilitado` existe pelo recorte por diretoria, que só a API vai saber
 * fazer: quem é só DRE não pode receber a caixa inteira. Desligar a consulta,
 * e não filtrar depois, é o que impede o dado de todas as DREs de chegar ao
 * navegador de quem só pode ver uma.
 */
export function useCaixaDeMensagens(
  opcoes: { habilitado?: boolean } = {},
): UseQueryResult<ConversaDaMedicao[], ErroApi> {
  return useQuery<ConversaDaMedicao[], ErroApi>({
    queryKey: CHAVES_DE_MENSAGENS.caixa,
    enabled: opcoes.habilitado ?? true,
    queryFn: carregarCaixaDeMensagens,
    retry: naoInsistirEmRecusa,
  });
}

export function useDresDasMensagens(
  opcoes: { habilitado?: boolean } = {},
): UseQueryResult<readonly DreDaMensagem[], ErroApi> {
  return useQuery<readonly DreDaMensagem[], ErroApi>({
    queryKey: CHAVES_DE_MENSAGENS.dres,
    enabled: opcoes.habilitado ?? true,
    queryFn: async () => DRES,
    staleTime: Infinity,
  });
}

export function useMensagensDaMedicao(
  medicaoId: string | null,
): UseQueryResult<MensagemDaMedicao[], ErroApi> {
  return useQuery<MensagemDaMedicao[], ErroApi>({
    queryKey: CHAVES_DE_MENSAGENS.conversa(medicaoId ?? ''),
    enabled: medicaoId !== null,
    queryFn: () => carregarMensagensDaMedicao(medicaoId ?? ''),
    retry: naoInsistirEmRecusa,
  });
}
